import { useCallback, useEffect, useRef, useState } from 'react';
import { BGM_MOOD_SECTIONS, type ProgramSlideBgmPick } from '../components/program/ProgramSlideBgmModal';
import type { RemoteBgmPick } from '../types/presentationRemote';
import {
  resolveSlideBgmAudioSrc,
  SLIDE_BGM_MOOD_IDS,
  SLIDE_BGM_TRACKS,
  slideBgmTracksForMood,
  startSlideBgmPlaybackAsync,
  type SlideBgmMoodId,
  type SlideBgmPlaybackHandle,
  type SlideBgmTrack,
} from '../utils/slideBgmPlayback';

export type MainPresentationBgmTransport = 'idle' | 'playing' | 'paused';

const LAST_PICK_LS = 'cp-main-pres-bgm-last-pick-v1';

export function readLastPick(): { moodId: SlideBgmMoodId; track: SlideBgmTrack } {
  try {
    const raw = window.localStorage.getItem(LAST_PICK_LS);
    const v = raw ? (JSON.parse(raw) as { moodId?: string; track?: number }) : null;
    if (
      v &&
      (SLIDE_BGM_MOOD_IDS as ReadonlyArray<string>).includes(String(v.moodId)) &&
      (SLIDE_BGM_TRACKS as ReadonlyArray<number>).includes(Number(v.track))
    ) {
      return { moodId: v.moodId as SlideBgmMoodId, track: Number(v.track) as SlideBgmTrack };
    }
  } catch {
    /* ignore */
  }
  return { moodId: 'calm', track: 1 };
}

export function writeLastPick(moodId: SlideBgmMoodId, track: SlideBgmTrack) {
  try {
    window.localStorage.setItem(LAST_PICK_LS, JSON.stringify({ moodId, track }));
  } catch {
    /* ignore */
  }
}

export type RemoteBgmResult = { ok: true } | { ok: false; error: 'autoplay_blocked' | 'unknown' };

/**
 * 메인 프레젠테이션 BGM 상태·제어 (툴바와 리모컨 공용 — 동작 중복 없이 같은 핸들러 사용)
 */
export function useMainPresentationBgm() {
  const [bgmModalOpen, setBgmModalOpen] = useState(false);
  const [bgmTransport, setBgmTransport] = useState<MainPresentationBgmTransport>('idle');
  /** 자동재생 정책으로 재생이 막힌 상태 (화면 터치 필요) */
  const [bgmBlocked, setBgmBlocked] = useState(false);
  const duckedRef = useRef(false);
  const bgmPlaybackRef = useRef<SlideBgmPlaybackHandle | null>(null);
  const startSeqRef = useRef(0);
  /** 현재 플레이어에 올라간 곡 */
  const currentRef = useRef<{ moodId: SlideBgmMoodId; track: SlideBgmTrack } | null>(null);

  const startTrack = useCallback(async (moodId: SlideBgmMoodId, track: SlideBgmTrack): Promise<boolean> => {
    const seq = ++startSeqRef.current;
    bgmPlaybackRef.current?.stop();
    bgmPlaybackRef.current = null;
    writeLastPick(moodId, track);
    const src = resolveSlideBgmAudioSrc(moodId, track);
    const handle = await startSlideBgmPlaybackAsync(src, { moodId, track });
    if (seq !== startSeqRef.current) {
      handle.stop();
      return false;
    }
    bgmPlaybackRef.current = handle;
    currentRef.current = { moodId, track };
    if (duckedRef.current) handle.setVolume(0.25);
    setBgmTransport('playing');
    const ok = await handle.initialPlay;
    if (seq !== startSeqRef.current) return ok;
    if (!ok) {
      setBgmBlocked(true);
      setBgmTransport('paused');
    } else {
      setBgmBlocked(false);
    }
    return ok;
  }, []);

  const handleBgmPick = useCallback(
    async (pick: ProgramSlideBgmPick) => {
      const tracks = slideBgmTracksForMood(pick.moodId);
      const track = pick.mode === 'random' ? tracks[Math.floor(Math.random() * tracks.length)] : pick.track;
      const ok = await startTrack(pick.moodId, track);
      setBgmModalOpen(false);
      return ok;
    },
    [startTrack]
  );

  const handleBgmPauseToggle = useCallback(() => {
    const h = bgmPlaybackRef.current;
    if (!h) return;
    if (h.isPaused()) {
      h.resume();
      setBgmTransport('playing');
      setBgmBlocked(false);
    } else {
      h.pause();
      setBgmTransport('paused');
    }
  }, []);

  const handleBgmStop = useCallback(() => {
    startSeqRef.current += 1;
    bgmPlaybackRef.current?.stop();
    bgmPlaybackRef.current = null;
    currentRef.current = null;
    setBgmTransport('idle');
    setBgmBlocked(false);
  }, []);

  /* —— 리모컨 —— */
  const remotePlay = useCallback(
    async (opt?: { pick?: RemoteBgmPick; restart?: boolean }): Promise<RemoteBgmResult> => {
      try {
        const rp = opt?.pick;
        const cur = bgmPlaybackRef.current;
        /* 리모컨이 고른 곡과 같은 곡이 올라가 있으면 재개/유지, 다르면 그 곡으로 전환 */
        if (rp && !opt?.restart && cur && sameBgmPick(currentRef.current, rp)) {
          if (!cur.isPaused()) return { ok: true };
          const ok = await cur.resumeAsync();
          setBgmBlocked(!ok);
          setBgmTransport(ok ? 'playing' : 'paused');
          return ok ? { ok: true } : { ok: false, error: 'autoplay_blocked' };
        }
        if (rp) {
          const section = BGM_MOOD_SECTIONS.find((x) => x.id === rp.moodId);
          if (!section) return { ok: false, error: 'unknown' };
          const tracks = slideBgmTracksForMood(section.id);
          const pick: ProgramSlideBgmPick =
            rp.mode === 'track' && (tracks as ReadonlyArray<number>).includes(Number(rp.track))
              ? { moodId: section.id, moodLabel: section.label, mode: 'track', track: Number(rp.track) as SlideBgmTrack }
              : { moodId: section.id, moodLabel: section.label, mode: 'random' };
          /* 툴바와 같은 경로 */
          const ok = await handleBgmPick(pick);
          return ok ? { ok: true } : { ok: false, error: 'autoplay_blocked' };
        }
        const h = bgmPlaybackRef.current;
        if (h) {
          if (!h.isPaused()) return { ok: true };
          const ok = await h.resumeAsync();
          if (ok) {
            setBgmTransport('playing');
            setBgmBlocked(false);
            return { ok: true };
          }
          setBgmBlocked(true);
          setBgmTransport('paused');
          return { ok: false, error: 'autoplay_blocked' };
        }
        const last = readLastPick();
        const ok = await startTrack(last.moodId, last.track);
        return ok ? { ok: true } : { ok: false, error: 'autoplay_blocked' };
      } catch {
        return { ok: false, error: 'unknown' };
      }
    },
    [startTrack, handleBgmPick]
  );

  /** 읽어주기 중 BGM 볼륨 낮추기 */
  const setBgmDucked = useCallback((on: boolean) => {
    duckedRef.current = on;
    bgmPlaybackRef.current?.setVolume(on ? 0.25 : 1);
  }, []);

  const remotePause = useCallback((): RemoteBgmResult => {
    const h = bgmPlaybackRef.current;
    if (h && !h.isPaused()) h.pause();
    if (h) setBgmTransport('paused');
    setBgmBlocked(false);
    return { ok: true };
  }, []);

  /** 자동재생 차단 시 화면 첫 터치(사용자 제스처)에서 재생 */
  const resumeFromGesture = useCallback(async () => {
    const h = bgmPlaybackRef.current;
    if (!h) {
      setBgmBlocked(false);
      return;
    }
    const ok = await h.resumeAsync();
    setBgmBlocked(!ok);
    if (ok) setBgmTransport('playing');
  }, []);

  useEffect(() => {
    return () => {
      bgmPlaybackRef.current?.stop();
      bgmPlaybackRef.current = null;
    };
  }, []);

  return {
    bgmModalOpen,
    setBgmModalOpen,
    bgmTransport,
    bgmBlocked,
    setBgmDucked,
    handleBgmPick,
    handleBgmPauseToggle,
    handleBgmStop,
    remotePlay,
    remotePause,
    remoteStop: handleBgmStop,
    resumeFromGesture,
  };
}

export type MainPresentationBgmController = ReturnType<typeof useMainPresentationBgm>;

/** 리모컨 음악 명령을 처리하는 최소 인터페이스 — 페이지가 자체 BGM 플레이어로 구현 가능(수업 프레젠) */
/** 지금 플레이어의 곡이 리모컨 pick과 같은지 (랜덤은 같은 분위기면 같은 곡으로 봄) */
export function sameBgmPick(cur: { moodId: string; track: number } | null, rp: RemoteBgmPick): boolean {
  if (!cur || cur.moodId !== rp.moodId) return false;
  return rp.mode === 'random' || Number(rp.track) === cur.track;
}

export type RemoteBgmController = Pick<
  MainPresentationBgmController,
  'remotePlay' | 'remotePause' | 'remoteStop' | 'setBgmDucked' | 'bgmBlocked' | 'resumeFromGesture'
>;

/** 리모컨 pick → 툴바/모달과 같은 pick (알 수 없는 분위기면 null) */
export function remotePickToSlidePick(rp: RemoteBgmPick): ProgramSlideBgmPick | null {
  const section = BGM_MOOD_SECTIONS.find((x) => x.id === rp.moodId);
  if (!section) return null;
  const tracks = slideBgmTracksForMood(section.id);
  return rp.mode === 'track' && (tracks as ReadonlyArray<number>).includes(Number(rp.track))
    ? { moodId: section.id, moodLabel: section.label, mode: 'track', track: Number(rp.track) as SlideBgmTrack }
    : { moodId: section.id, moodLabel: section.label, mode: 'random' };
}
